/* eslint-disable max-len */
import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';
import { roomsArraySelector } from '../roomMeasure';
import { stairs } from '../stairs';
import { selectedCarpetPad } from '../carpetPad';
import { configSelector } from '../config/configSelector';

const missingCarpetAndRooms = '(Calculated after selecting a carpet and entering room/stair information)';
const missingCarpetSelectionOnly = '(Calculated after selecting a carpet)';
const missingRoomsMeasurementsOnly = '(Calculated after entering room/stair information)';
const regionalSelections = (state) => state.store.regionalSelections;
const getChoices = (state) => state.configurator.choices;

export const pricingNodeSelector = createSelector(
  (state) => state.pricing,
  (pricing) => pricing
);

export const regionalSelectionsChoiceSelector = createSelector(
  [getChoices],
  (choices) => {
    if (Object.keys(choices).length > 1) {
      const matchingChoice = Object.values(choices)
        .find((choice) => choice && choice.internalName === 'Region_CarpetInstallCharge');

      if (matchingChoice) {
        return matchingChoice;
      }
    }
    return null;
  }
);

export const getRegionalCarpetInstallPrice = createSelector(
  [regionalSelections, regionalSelectionsChoiceSelector],
  (regionalSelectionsArray, regionalChoice) => {
    if (regionalChoice && regionalSelectionsArray?.Selections) {
      const matchingSelection = regionalSelectionsArray.Selections.find(
        (selection) => selection.ChoiceId === regionalChoice.id
      );

      if (matchingSelection) {
        const metaDataItem = regionalChoice.metaDataItems.find((item) => item.name === 'CarpetInstallPrice');
        if (metaDataItem) {
          return metaDataItem.value;
        }
      }
    }
    return null;
  }
);

export const selectSelectedCarpetChoice = createSelector(
  pricingNodeSelector,
  (pricing) => pricing.selectedChoice
);
export const choicePrices = createSelector(
  pricingNodeSelector,
  (pricing) => pricing.skuPrices,
);

export const hasSelectedCarpetSelector = createSelector(
  pricingNodeSelector,
  ({ selectedChoice }) => !!selectedChoice?.id && selectedChoice?.isAvailable
);

export const estimateErrorMsgSelector = createSelector(
  [selectSelectedCarpetChoice, roomsArraySelector],
  (selectedChoice, rooms) => {
    const missingRoomInformation = isEmpty(rooms);
    const missingCarpetSelection = selectedChoice === '' || isEmpty(selectedChoice);
    if (missingRoomInformation && missingCarpetSelection) {
      return missingCarpetAndRooms;
    }
    if (!missingRoomInformation && missingCarpetSelection) {
      return missingCarpetSelectionOnly;

    }
    if (missingRoomInformation && !missingCarpetSelection) {
      return missingRoomsMeasurementsOnly;
    }
    return '';
  }
);

const totalEstimateArea = createSelector(
  [roomsArraySelector, stairs],
  (roomMeasures, step) => {
    // eslint-disable-next-line
    const areaOfAllRooms = roomMeasures.reduce((acc, curVal) => acc += curVal.sqFt, 0);
    const stairSqFt = step?.stairsSquareFeet || 0;
    return areaOfAllRooms + stairSqFt;
  },
);

export const totalCarpetPrice = createSelector(
  [selectSelectedCarpetChoice, totalEstimateArea],
  (selectedCarpet, totalSqFt) => {
    const carpetPrice = selectedCarpet?.priceSquareYard || 0;
    return ((totalSqFt * carpetPrice) / 9).toFixed(2);
  },
);

export const totalCarpetPadPrice = createSelector(
  [selectedCarpetPad, totalEstimateArea],
  (carpetPad, totalSqFt) => {
    const selectedCarpetPadPrice = carpetPad?.padPrice || 0;
    return (totalSqFt * selectedCarpetPadPrice).toFixed(2);
  },
);

export const totalRegionalInstallPrice = createSelector(
  [getRegionalCarpetInstallPrice, totalEstimateArea],
  (regionalPrice, totalSqFt) => {
    const regionalInstallPrice = regionalPrice ? parseFloat(regionalPrice) : 0;
    return (totalSqFt * regionalInstallPrice).toFixed(2);
  },
);

export const totalProjectPrice = createSelector(
  [totalCarpetPadPrice, totalCarpetPrice, totalRegionalInstallPrice],
  (pad, carpet, regionalInstall) => {
    const total = (parseFloat(pad) + parseFloat(carpet) + parseFloat(regionalInstall)).toFixed(2);
    return total;
  },
);

export const installationThresholdMetSelector = createSelector(
  [totalProjectPrice, configSelector],
  (total, { installationThresholdValue }) => (total * 1) > installationThresholdValue
);

export const projectTotalsSelector = createSelector(
  [totalCarpetPrice, totalCarpetPadPrice, totalProjectPrice, totalRegionalInstallPrice],
  (carpetTotalPrice, totalPadPrice, total, totalRegionalInstall) => ({
    carpetTotalPrice,
    totalPadPrice,
    total,
    totalRegionalInstall,
  })
);
